import { useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import type {
  InferenceDistributionEntry,
  InferredMixEditorProps,
} from '@/types/components/audiences/inferred-mix-editor'

export function InferredMixEditor({
  entries,
  onChange,
  disabled = false,
  showTotal = true,
  className,
}: InferredMixEditorProps) {
  const total = useMemo(
    () => entries.reduce((sum, e) => sum + e.percentage, 0),
    [entries],
  )

  const handleSliderChange = useCallback(
    (index: number, rawValue: number) => {
      const clamped = Math.max(0, Math.min(100, Math.round(rawValue)))
      onChange(
        entries.map((entry, i) =>
          i === index ? { ...entry, percentage: clamped } : entry,
        ),
      )
    },
    [entries, onChange],
  )

  const remainder = 100 - total
  const totalColor =
    total === 100
      ? 'text-emerald-600 dark:text-emerald-400'
      : total > 100
        ? 'text-destructive'
        : 'text-rose-400'

  const totalLabel =
    total === 100
      ? 'Total 100%'
      : total > 100
        ? `-${total - 100}% needed  Total ${total}%`
        : `+${remainder}% needed  Total ${total}%`

  return (
    <div className={cn('space-y-5', className)}>
      {showTotal ? (
        <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/30 px-4 py-2.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Total
          </span>
          <span className={cn('text-sm font-semibold tabular-nums', totalColor)}>
            {totalLabel}
          </span>
        </div>
      ) : null}
      {entries.map((entry, index) => {
        return (
          <div key={entry.value} className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2 text-base font-medium tracking-tight text-foreground dark:text-white">
              <span>{entry.value}</span>
              <span className="tabular-nums">
                {entry.percentage}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={entry.percentage}
              disabled={disabled}
              onChange={(e) => handleSliderChange(index, Number(e.target.value))}
              className="identity-slider h-2.5 w-full"
            />
          </div>
        )
      })}
    </div>
  )
}
